/**
 * services/web-ui/src/components/audit/StageTooltip.tsx
 *
 * Hover-card for a stage block — shows description, role, event count, duration.
 * Uses @radix-ui/react-tooltip (Radix primitive, no extra styling lib needed).
 */

import * as RadixTooltip from "@radix-ui/react-tooltip";
import { getStageInfo, getStageColors } from "../../lib/stage-descriptions";
import type { SessionEvent } from "../../api/voice-sessions";

interface Props {
  stage: string;
  events: SessionEvent[];
  durationMs: number;
  children: React.ReactNode;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const min = Math.floor(ms / 60_000);
  const sec = Math.round((ms % 60_000) / 1000);
  return `${min}m ${sec}s`;
}

export function StageTooltip({ stage, events, durationMs, children }: Props) {
  const info = getStageInfo(stage);
  const colors = getStageColors(stage);
  const stageEvents = events.filter((e) => e.stage === stage || e.payload?.["stage"] === stage);

  return (
    <RadixTooltip.Provider delayDuration={300}>
      <RadixTooltip.Root>
        <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <RadixTooltip.Content
            side="top"
            sideOffset={6}
            className="z-50 rounded-lg border border-gray-700 bg-gray-900 px-3 py-2.5 shadow-xl text-sm max-w-xs"
          >
            <div className="space-y-1.5">
              {/* Stage name + role badge */}
              <div className="flex items-center gap-2">
                <span className="font-semibold text-gray-200">{stage}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded ${colors.bg} ${colors.text} border ${colors.border}`}>
                  {info.role}
                </span>
              </div>

              {/* Description */}
              <p className="text-gray-400 text-xs leading-relaxed">{info.description}</p>

              {/* Stats */}
              <div className="flex gap-3 pt-1 border-t border-gray-800 text-xs text-gray-500">
                <span>{formatDuration(durationMs)}</span>
                <span>{stageEvents.length} event{stageEvents.length === 1 ? "" : "s"}</span>
              </div>
            </div>
            <RadixTooltip.Arrow className="fill-gray-700" />
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
}
